import { Injectable } from '@angular/core';
import {
  LBSErrorHandler,
  LBSErrorHandlerResult,
} from '../../error-handler/error-handler';
import { LoggingService } from '../../logging/logic/logging.service';
import { PeripheralError } from './peripheral-errors';
import { RequestBTPermissionAction } from '../../action-interpreter/action-handlers/request-bluetooth-permission-action-handler.service';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import { RequestWifiPermissionAction } from '../../action-interpreter/action-handlers/request-wifi-permission-action-handler.service';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class PeripheralErrorHandler implements LBSErrorHandler {
  // Lifecycle
  constructor(private logger: LoggingService) {}

  public async handleError(error: Error): Promise<LBSErrorHandlerResult> {
    if (
      Object.values(PeripheralError).includes(error?.message as PeripheralError)
    ) {
      return await this.getErrorHandlerResultForKey(
        error.message as PeripheralError,
      );
    }
    this.logger.error(
      this.constructor.name,
      'given error',
      error?.message,
      'is not of type PeripheralError',
    );
    return undefined;
  }

  // Private Functions
  public async getErrorHandlerResultForKey(
    error: PeripheralError,
  ): Promise<LBSErrorHandlerResult> {
    const platform = Capacitor.getPlatform();
    switch (error) {
      case PeripheralError.UNKNOWN_DEVICE_TYPE:
        return {
          alert: {
            title: 'ERROR-HANDLERS.PERIPHERAL-ERRORS.UNKNOWN_DEVICE_TYPE.TITLE',
            subtitle:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.UNKNOWN_DEVICE_TYPE.SUBTITLE',
            message:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.UNKNOWN_DEVICE_TYPE.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.UNKNOWN_DEVICE_TYPE.CONFIRM',
            confirmFunc: () => {},
            showExportLog: false,
          },
        };
      case PeripheralError.BT_PERMISSION_PROMPT:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_PROMPT.TITLE',
            subtitle:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_PROMPT.SUBTITLE',
            message:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_PROMPT.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_PROMPT.CONFIRM',
            confirmAction: RequestBTPermissionAction,
            cancel:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_PROMPT.CANCEL',
            cancelFunc: () => {
              console.log('Prealert cancel pressed, cannot request permission');
            },
            showExportLog: false,
          },
        };
      case PeripheralError.BT_PERMISSION_DENIED:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_DENIED.TITLE',
            subtitle:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_DENIED.SUBTITLE',
            message:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_DENIED.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_DENIED.CONFIRM',
            confirmFunc: () => {
              this.openSettings();
            },
            cancel:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.BT_PERMISSION_DENIED.CANCEL',
            cancelFunc: () => {
              console.log(
                'Prealert cancel pressed, cannot redirect to settings',
              );
            },
            showExportLog: false,
          },
        };
      case PeripheralError.WIFI_PERMISSION_PROMPT:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.TITLE',
            subtitle:
              platform === 'ios'
              ? 'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.SUBTITLE.IOS'
              : 'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.SUBTITLE.ANDROID',
            message: 'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.CONFIRM',
            confirmAction: RequestWifiPermissionAction,
            cancel:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_PROMPT.CANCEL',
            cancelFunc: () => {
              console.log('Prealert cancel pressed, cannot request permission');
            },
            showExportLog: false,
          },
        };
      case PeripheralError.WIFI_PERMISSION_DENIED:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.TITLE',
            subtitle:
              platform === 'ios'
              ? 'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.SUBTITLE.IOS'
              : 'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.SUBTITLE.ANDROID',
            message: 
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.CONFIRM',
            confirmFunc: () => {
              this.openSettings();
            },
            cancel:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.WIFI_PERMISSION_DENIED.CANCEL',
            cancelFunc: () => {
              console.log(
                'Prealert cancel pressed, cannot redirect to settings',
              );
            },
            showExportLog: false,
          },
        };
      case PeripheralError.RICOH_CONNECTION_ERROR:
      case PeripheralError.RICOH_ANDROID_UNAVAILABLE:
      case PeripheralError.RICOH_ANDROID_CONNECTION_ERROR:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_CONNECTION_ERROR.TITLE',
            subtitle:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_CONNECTION_ERROR.SUBTITLE',
            message:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_CONNECTION_ERROR.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_CONNECTION_ERROR.CONFIRM',
            confirmFunc: () => {
            },
            showExportLog: false,
          },
        };
      case PeripheralError.RICOH_USERAPPROVAL_DENIED:
        return {
          alert: {
            title:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_USERAPPROVAL_DENIED.TITLE',
            subtitle:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_USERAPPROVAL_DENIED.SUBTITLE',
            message:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_USERAPPROVAL_DENIED.MESSAGE',
            confirm:
              'ERROR-HANDLERS.PERIPHERAL-ERRORS.RICOH_USERAPPROVAL_DENIED.CONFIRM',
            confirmFunc: () => {
            },
            showExportLog: false,
          },
        };
      default:
        this.logger.error(
          this.constructor.name,
          'Not Handled PeripheralError found!',
          error,
        );
        return undefined;
    }
  }

  private async openSettings() {
    NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  }
}
